import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import supabase from '../../supabase';
import './InvoiceStatusTracker.css';

const COLORS = ['#10B981', '#F59E0B', '#EF4444']; // Paid, Unpaid, Overdue

const InvoiceStatusTracker = ({ user, onSectorClick }) => {
  const navigate = useNavigate();
  const [invoiceData, setInvoiceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [stats, setStats] = useState({
    totalInvoices: 0,
    pendingInvoices: 0,
    paidInvoices: 0,
  });

  useEffect(() => {
    if (user?.id) {
      fetchInvoiceStatusData();
    }
  }, [user?.id]);

  const fetchInvoiceStatusData = async () => {
    try {
      setLoading(true);
      setHasError(false);

      const { data: allInvoices, error } = await supabase
        .from('invoices')
        .select('id, status, due_date')
        .eq('user_id', user.id);

      if (error) throw error;

      const now = new Date();
      let paid = 0;
      let unpaid = 0;
      let overdue = 0;

      allInvoices.forEach(invoice => {
        if (invoice.status.toLowerCase() === 'paid') {
          paid += 1;
        } else if (invoice.status.toLowerCase() === 'unpaid') {
          const dueDate = new Date(invoice.due_date);
          if (dueDate < now) {
            overdue += 1;
          } else {
            unpaid += 1;
          }
        }
      });

      setInvoiceData(
        [
          { name: 'Paid', value: paid },
          { name: 'Unpaid', value: unpaid },
          { name: 'Overdue', value: overdue },
        ].filter(item => item.value > 0)
      );

      setStats({
        totalInvoices: allInvoices.length,
        pendingInvoices: unpaid + overdue,
        paidInvoices: paid,
      });

    } catch (error) {
      console.error('Error fetching invoice status:', error);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  const totalInvoices = stats.totalInvoices;

  const handleSectorClick = (data, index) => {
    const selectedStatus = data.name; // Paid, Unpaid, Overdue
    onSectorClick && onSectorClick('invoice-tracking', selectedStatus);
  };

  const handleCreateInvoice = () => {
    // Use the onSectorClick callback to switch to the invoice-generation tab
    onSectorClick && onSectorClick('invoice-generation');
  };

  if (loading) {
    return (
      <div className="invoicestatustracker">
        <p>Loading invoice status...</p>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="invoicestatustracker">
        <p>Failed to load invoice status.</p>
        <button onClick={fetchInvoiceStatusData} className="retry-button">Retry</button>
      </div>
    );
  }

  return (
    <div className="invoicestatustracker-section">
      <div className="invoicestatustracker-section-header">
        <h2 className="invoicestatustracker-section-title">Invoice Status</h2>
        <button 
          className="invoicestatustracker-create-btn"
          onClick={handleCreateInvoice}
        >
          Create Invoice
        </button>
      </div>

      <div className="invoicestatustracker-content">
        <div className="invoicestatustracker-summary">
          <div className="invoicestatustracker-summary-card invoicestatustracker-paid">
            <p className="invoicestatustracker-summary-label">Total Invoices</p>
            <h3 className="invoicestatustracker-summary-count">{stats.totalInvoices}</h3>
          </div>
          <div className="invoicestatustracker-summary-card invoicestatustracker-unpaid">
            <p className="invoicestatustracker-summary-label">Pending Invoices</p>
            <h3 className="invoicestatustracker-summary-count">{stats.pendingInvoices}</h3>
          </div>
          <div className="invoicestatustracker-summary-card invoicestatustracker-overdue">
            <p className="invoicestatustracker-summary-label">Paid Invoices</p>
            <h3 className="invoicestatustracker-summary-count">{stats.paidInvoices}</h3>
          </div>
        </div>

        <div className="invoicestatustracker-chart-container">
          {totalInvoices > 0 ? (
            <div className="invoicestatustracker-chart-wrapper">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={invoiceData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={80}
                    outerRadius={140}
                    paddingAngle={0}
                  >
                    {invoiceData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                        onClick={() => handleSectorClick(entry, index)}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value} Invoices`, name]} />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="invoicestatustracker-no-data">
              <p>No invoices found.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceStatusTracker;